import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  SelectChangeEvent,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  IconCalendarClock,
  IconUsers,
  IconBuildingEstate,
  IconStethoscope,
  IconCertificate,
  IconBus,
  IconPlayerPlayFilled,
  IconFileDescription,
  IconBriefcase2,
  IconReportMedical,
  IconHelp,
  IconReport,
  IconFolderOpen,
  IconChartBar,
} from "@tabler/icons-react";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationBell from "../custom/NotificationBell";
import DrawerData from "./DrawerData";
import AvatarComponent from "./AvatarComponent";
import { useClinicOffices } from "../../contexts/ClinicContext";
import {
  getClinics,
  getClinicUserConnectedClinics,
  inviteDriverUser,
} from "../../api/clinicServices";
import { getUser } from "../../utils/storage";
import ClinicOfficeSelect from "./ClinicOfficeSelect";
import LanguageSwitcher from "./LanguageSwitcher";
import SnackbarAlert from "../custom/SnackbarAlter";
import { usPhoneNumberPattern } from "../../utils/inputCheck";

const drawerWidth = 250;
const miniDrawerWidth = 60;

interface InviteObject {
  email?: string;
  role: string;
  phone_number?: string;
  type?: string;
}

interface Notification {
  id: number;
  message: string;
  read: boolean;
}

interface ClinicOffice {
  pkid: number;
  id?: string;
  state?: string;
  city?: string;
  address: string;
}

export interface Clinic {
  id: number;
  name: string;
  clinic_offices: ClinicOffice[];
}

interface HeaderProps {
  miniMode: boolean;
  handleMiniToggle: () => void;
  hasAppointments?: boolean;
}

interface State {
  open: boolean;
  data: string;
  severity: "error" | "success";
  variant: "filled" | "outlined" | "standard";
}

const Header: React.FC<HeaderProps> = ({
  miniMode,
  handleMiniToggle,
  hasAppointments,
}) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [connectedClinics, setConnectedClinicsState] = useState<Clinic[]>([]);
  const { clinicOfficeId, setClinicOfficeId } = useClinicOffices();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [filteredItems, setFilteredItems] = useState([]);
  const [openInvite, setOpenInvite] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [inviteMethod, setInviteMethod] = useState<'email' | 'phone'>('email');
  const [phoneNumber, setPhoneNumber] = useState("");
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // phone size

  const [alert, setAlert] = useState<State>({
    open: false,
    data: "",
    severity: "error",
    variant: "filled",
  });

  const handleAlert = (message: string, severity: "error" | "success") => {
    setAlert({ open: true, data: message, severity, variant: "filled" });
  };

  const handleClose = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const navigate = useNavigate();

  const handleOpenInvite = () => {
    // setMessage(null);
    setOpenInvite(true);
  };
  const handleCloseInvite = () => {
    setOpenInvite(false);
    setEmail("");
  };
  const handleSubmitInvite = async () => {
    if (inviteMethod === 'email' && !email) return;
    if (inviteMethod === 'phone' && !phoneNumber) return;

    if (inviteMethod === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      handleAlert("Enter a valid email address.", "error");
      return;
    }
    if (inviteMethod === 'phone' && !usPhoneNumberPattern.test(phoneNumber)) {
      handleAlert("Enter a valid phone number.", "error");
      return;
    }

    setLoading(true);
    try {
      const inviteObject: InviteObject = { role: "Driver" };

      if (inviteMethod === 'phone') {
        inviteObject.phone_number = phoneNumber.replace(/\D/g, "");
      } else {
        inviteObject.email = email;
      }

      // Attempt to invite the user
      const res = await inviteDriverUser(inviteObject);

      if (res.activation_link === null) {
        handleAlert("Reminder sent successfully! 🎉", "success");
      } else {
        handleAlert("Driver invited successfully! 🎉", "success");
      }
      handleCloseInvite();
      setEmail("");
      setPhoneNumber("");
    } catch (error: any) {
      handleAlert(error.response?.data?.message || "Failed to send invitation. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const userRoles = (getUser() || {}).roles?.map(
          (role: any) => role.name
        ) || [];

        if (userRoles.includes("Clinic")) {
          // Clinic-specific API call
          const clinics = await getClinics();
          const clinicData = clinics.map((clinic) => ({
            ...clinic,
            clinic_offices: clinic.clinic_offices.map((office) => ({
              address: office.address,
              pkid: Number(office.pkid),
              city: office.city,
              state: office.state,
            })),
          }));
          setConnectedClinicsState(clinicData);
        } else if (
          userRoles.includes("Provider") ||
          userRoles.includes("Medical Assistant")
        ) {
          const providerClinics = await getClinicUserConnectedClinics();
          const clinicData = providerClinics.map((clinic) => ({
            ...clinic,
            clinic_offices: clinic.clinic_offices.map((office) => ({
              address: office.address,
              pkid: Number(office.pkid),
              city: office.city,
              state: office.state,
            })),
          }));
          setConnectedClinicsState(clinicData);
        }
      } catch (error) {
        console.error("Error fetching clinics:", error);
      }
    };

    fetchClinics();
  }, []);

  useEffect(() => {
    const userRoles = (getUser() || {}).roles?.map((r: any) => r.name) || [];

    // Helper function to filter items based on user roles
    const filterItems = (items: any) =>
      items
        .filter(
          (item: any) =>
            !item.roleBased ||
            item.roleBased.some((role: any) => userRoles.includes(role))
        )
        .map((item: any) => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }));

    const items = [
      {
        id: "dashboard",
        text: "Dashboard",
        icon: <IconCalendarClock />,
        path: "/dashboard",
        default: true,
      },
      {
        id: "user",
        text: "Management",
        icon: <IconUsers />,
        roleBased: ["Clinic"],
        children: [
          {
            id: "analytics",
            text: "Analytics",
            icon: <IconChartBar />,
            path: "/user/analytics",
            roleBased: ["Clinic"],
          },
          {
            id: "clinic-office",
            text: "Clinic Office",
            icon: <IconBuildingEstate />,
            path: "/user/clinic-office",
            roleBased: ["Clinic"],
          },
          {
            id: "clinic-user",
            text: "Clinic User",
            icon: <IconStethoscope />,
            path: "/user/clinic-user",
            roleBased: ["Clinic"],
          },
          {
            id: "invited-drivers",
            text: "Manage Drivers",
            icon: <IconBus />,
            path: "/user/invited-driver",
            roleBased: ["Clinic"],
          },
        ],
      },
      {
        id: "nrcme",
        text: "NRCME Data",
        icon: <IconReportMedical />,
        path: "/nrcme",
        roleBased: ["Provider", "Medical Assistant", "Clinic"],
      },
      {
        id: "physical",
        text: "Start Physical",
        icon: <IconPlayerPlayFilled />,
        path: "/physical",
        roleBased: ["Driver"],
      },
      {
        id: "forms",
        text: "Forms",
        icon: <IconFileDescription />,
        path: "/forms",
        roleBased: ["Provider", "Medical Assistant", "Clinic"],
      },
      {
        id: "resources",
        text: "Resources",
        icon: <IconFolderOpen />,
        path: "/resources",
        roleBased: ["Provider", "Medical Assistant", "Clinic"],
      },
      {
        id: "vitals",
        text: "Vitals",
        icon: <IconReport />,
        path: "/vitals",
        roleBased: ["Driver"],
      },
      {
        id: "documents_driver",
        text: "Documents",
        icon: <IconBriefcase2 />,
        path: "/documents-driver",
        roleBased: ["Driver"],
      },
      {
        id: "documents",
        text: "Documents",
        icon: <IconBriefcase2 />,
        path: "/documents",
        roleBased: ["Provider", "Clinic", "Medical Assistant"],
      },
      {
        id: "user-history",
        text: "Medical History",
        icon: <IconReportMedical />,
        path: "/user-history",
        roleBased: ["Driver"],
      },
      {
        id: "help",
        text: "Help",
        icon: <IconHelp />,
        path: "/help",
      },
    ];

    setFilteredItems(filterItems(items));
  }, []);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    setClinicOfficeId(selectedId);
  };

  const handleStartPhysical = async () => {
    navigate("/physical?autoStart=true");
  };

  const isDriver = (getUser() || {}).roles?.some(
    (role: any) => role.name === "Driver"
  );

  return (
    <>
      <SnackbarAlert
        open={alert.open}
        handleClose={handleClose}
        state={{ data: alert.data }}
        severity={alert.severity}
        variant={alert.variant}
      />
      <AppBar
        position="fixed"
        sx={{
          background: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(10px)",
          boxShadow: theme.shadows[4],
          width: {
            md: `calc(100% - ${miniMode ? miniDrawerWidth : drawerWidth}px)`,
          },
          ml: { md: `${miniMode ? miniDrawerWidth : drawerWidth}px` },
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              setOpen(!open);
              handleMiniToggle();
            }}
          >
            <MenuIcon sx={{ color: "black" }} />
          </IconButton>

          {/* Start Physical Button */}
          {isDriver && (
            <>
              {isSmallScreen ? (
                hasAppointments && (
                  <Button
                    onClick={handleStartPhysical}
                    variant="contained"
                    color="primary"
                    sx={{ ml: 2 }}
                  >
                    Start Physical
                  </Button>
                )
              ) : (
                <Button
                  onClick={handleStartPhysical}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  Start Physical
                </Button>
              )}
            </>
          )}

          {!(getUser() || {}).roles?.some(
            (role: any) => role.name === "Driver"
          ) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenInvite}
              >
                Invite Driver
              </Button>
            )}

          <Dialog
            open={openInvite}
            onClose={handleCloseInvite}
            disableEnforceFocus
          >
            <DialogTitle>Invite Driver</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Please choose how you want to invite the driver.
              </DialogContentText>

              <RadioGroup
                row
                value={inviteMethod}
                onChange={(e) => setInviteMethod(e.target.value as 'email' | 'phone')}
                sx={{ mt: 1, mb: 1 }}
              >
                <FormControlLabel value="email" control={<Radio />} label="Send via Email" />
                <FormControlLabel value="phone" control={<Radio />} label="Send via Phone Number" />
              </RadioGroup>

              {inviteMethod === 'email' ? (
                <TextField
                  autoFocus
                  margin="dense"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              ) : (
                <TextField
                  autoFocus
                  margin="dense"
                  label="Phone Number"
                  type="tel"
                  fullWidth
                  variant="outlined"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                />
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseInvite} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleSubmitInvite}
                color="primary"
                variant="contained"
                disabled={(inviteMethod === 'email' && !email) || (inviteMethod === 'phone' && !phoneNumber) || loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ flexGrow: 1 }} />
          {!(getUser() || {}).roles?.some(
            (role: any) => role.name === "Driver"
          ) && (
              <ClinicOfficeSelect
                clinicOfficeId={clinicOfficeId}
                connectedClinics={connectedClinics}
                onChange={handleChange}
              />
            )}
          {/* Language Switcher Dropdown */}
          <Box sx={{ ml: { xs: 0.5, sm: 2 } }}>
            <LanguageSwitcher />
          </Box>

          <NotificationBell
            unreadCount={notifications.filter((n) => !n.read).length}
            notifications={notifications}
            loading={false}
            onSettingsClick={() => console.log("Settings clicked")}
            color={theme.palette.text.primary}
          />
          <AvatarComponent />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: miniMode ? miniDrawerWidth : drawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop || open}
          onClose={() => setOpen(false)} // Close drawer on mobile when clicking outside
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "block" },
            "& .MuiDrawer-paper": {
              width: isDesktop
                ? miniMode
                  ? miniDrawerWidth
                  : drawerWidth
                : drawerWidth,
              background: "#001529",
              backdropFilter: "blur(8px)",
              color: theme.palette.common.white,
              boxShadow: theme.shadows[2],
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
              overflowY: "auto",
              overflowX: "hidden", // Prevent horizontal scrolling
            },
          }}
        >
          <DrawerData
            items={filteredItems}
            miniMode={miniMode}
            onItemClick={() => setOpen(false)}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default Header;
